import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { hubLabsGet } from "../utils/hubLabsApiRequest";
import Loading from "../components/Loading";
import wattbikeLogo from "../assets/img/wattbike_logo_410_white.png";
import wattbikeLogoBlack from "../assets/img/wattbike_logo_410_black.png";
import confetti from "../assets/img/confetti-01.png";
import iconClimb from "../assets/img/icon-climb.png";
import iconRace from "../assets/img/icon-race.png";
import iconTest from "../assets/img/icon-test.png";
import iconWorkout from "../assets/img/icon-workout.png";
import shareExample from "../assets/img/share_example.jpg";
import { AreaChart, Area, Cell, LineChart, Line, XAxis, BarChart, Bar, ResponsiveContainer } from 'recharts';
import './ThisYearSummary.css'; // needed to use media queries so holding css seperately
import { Container, Row, Col, Button, Carousel } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';


/**
 * ---------------------------------------------
 * ThisYearSummary notes
 * End of year infographic for a user
 * I apologies for how bad this code is, it was a rush job
 * 
 * ---------------------------------------------
 **/
function ThisYearSummary() {

  // doanload link
  const [downloadLink, setDownloadLink] = useState(false);

  // carousel index
  const [index, setIndex] = useState(0);

  // Get object ID from URL
  let { objectId } = useParams();

  // Get optional query params. 
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Set up some variables that will trigger UI updates
  const [loading, setLoading] = useState(true);
  const [noSessions, setNoSessions] = useState(true);
  const [isScreenshot, setIsScreenshot] = useState(searchParams.has("screenshot") ? true : false);
  const [yearResults, setYearResults] = useState({});
  const [processedResults, setProcessedResults] = useState({});

  const [width, setWidth] = useState(window.innerWidth);


  // get screenshot
  useEffect(() => {

    const getScreenshot = async () => {
      // compile all options
      let requestOptions = {
        resource: "screenshot",
        params: {
          campaign: '2024',
          userId: objectId,
          format: 'insta_story'
        },
        cache: false,
      };

      // run the query
      let response = await hubLabsGet(requestOptions);
      setDownloadLink(response.result);
      // console.log("screenshot response: ", response)
    }

    if (!noSessions) {
      // console.log("Getting screenshot")
      getScreenshot();
    }

  }, [noSessions]);


  // onload get data
  useEffect(() => {
    document.title = 'My Year on Wattbike';

    const fetchData = async () => {
      setLoading(true);

      let params = {
        userId: objectId,
        limit: 500
      }

      // get year results
      let sessionsResponse = await getUserSessions(params);
      if (!sessionsResponse.results) {
        setNoSessions(true);
        setLoading(false);
        return;
      } else {
        setNoSessions(false);

        // console.log("sessionsResponse: ", sessionsResponse);
        setYearResults(sessionsResponse.results);

        // process results
        let results = processResults(sessionsResponse.results);
        setProcessedResults(results);
      }


      setLoading(false);
    };
    fetchData().catch("Fetching sessions data error: ", console.error);
  }, []);


  // handle window resize
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  // add some additional info based on results and do some formatting for charts etc
  const processResults = (results) => {
    let processedResults = {};

    // active days based on start date
    processedResults.activeDays = countUniqueStartDates(results.rideSession_objs);

    // get word cloud and top list of session titles
    let wordCloudWorkouts = calcWordCloud(results.rideSession_objs);
    processedResults.wordCloudWorkoutsSorted = sortArrayOfObjectsDesc(wordCloudWorkouts);

    // get word cloud and top list of category titles
    let wordCloudCategories = calcWordCloud(results.category_objs);
    processedResults.wordCloudCategoriesSorted = sortArrayOfObjectsDesc(wordCloudCategories);
    // console.log("wordCloudCategoriesSorted: ", sortArrayOfObjectsDesc(wordCloudCategories));

    // build power avg chart data
    let chartDataOne = [];
    results.rideSummary_objs.forEach(ride => {
      chartDataOne.push({ name: ride._created_at, uv: ride.powerAvg });
    });
    processedResults.chartDataPowerAvg = chartDataOne;

    // build power peak chart data and get max
    chartDataOne = [];
    let maxPower = 0;
    results.rideSummary_objs.forEach(ride => {
      chartDataOne.push({ name: ride._created_at, uv: ride.powerMax });
      if (ride.powerMax > maxPower) { maxPower = ride.powerMax }
    });
    processedResults.chartDataPowerMax = chartDataOne;
    processedResults.maxPower = Math.round(maxPower);

    // build FTP chart data and get max
    chartDataOne = [];
    let maxFtp = 0;
    results.perfState_objs.forEach(perfState => {
      chartDataOne.push({ name: perfState._created_at, uv: perfState.ftp });
      if (perfState.ftp > maxFtp) { maxFtp = perfState.ftp }
    });
    processedResults.chartDataFtp = chartDataOne;
    processedResults.maxFtp = maxFtp;

    // build PowerKg chart data and get max
    chartDataOne = [];
    let maxPowerKg = 0;
    results.perfState_objs.forEach(perfState => {
      chartDataOne.push({ name: perfState._created_at, uv: perfState.powerKg });
      if (perfState.powerKg > maxPowerKg) { maxPowerKg = perfState.powerKg }
    });
    processedResults.chartDataPowerKg = chartDataOne;
    processedResults.maxPowerKg = maxPowerKg;

    // build weight chart data
    chartDataOne = [];
    results.perfState_objs.forEach(perfState => {
      chartDataOne.push({ name: perfState._created_at, uv: perfState.weight });
    });
    processedResults.chartDataWeight = chartDataOne;

    // build rides / month array for histogram
    let { histogramDataCount, peakMonth } = monthArray(results);
    processedResults.histogramDataCount = histogramDataCount;
    processedResults.peakMonth = peakMonth;

    // count climbs
    let climbCount = countCategoryTitles(results.category_objs, "Climbs");
    processedResults.climbCount = climbCount;

    // count quick ride
    let quickRideCount = countCategoryTitles(results.rideSession_objs, "Quick Ride");
    processedResults.quickRideCount = quickRideCount;

    // count tests
    let testCount = countCategoryTitles(results.category_objs, "Tests");
    processedResults.testsCount = testCount;
    // console.log("testCount: ", testCount)

    // count challenges
    let challengesCount = countCategoryTitles(results.category_objs, "Challenges");
    processedResults.challengesCount = challengesCount;
    // console.log("challengesCount: ", challengesCount)

    return processedResults;
  }


  // sort object by count desc
  const sortArrayOfObjectsDesc = (arr) => {
    return arr.sort((a, b) => b.count - a.count);
  };

  // count climbs by category title
  const countCategoryTitles = (array, searchString) => {
    let categoryCount = 0;
    array.forEach(category => {
      if (category.title === searchString) {
        categoryCount++;
      }
    });

    return categoryCount;
  }

  // count matching strings to use in a word cloud
  const calcWordCloud = (arr) => {

    const counterObject = {};

    arr.forEach(ele => {
      if (ele.title === undefined) { ele.title = 'Quick Ride' }

      if (counterObject[ele.title]) {
        counterObject[ele.title] += 1;
      } else {
        counterObject[ele.title] = 1;
      }
    });

    // convery to array of objects and return
    return Object.entries(counterObject).map(([title, count]) => ({ title, count }));
  }


  // build month array for histogram
  const monthArray = (data) => {
    const monthCountArr = new Array(12).fill(0);
    data.rideSession_objs.forEach(({ startDate }) => monthCountArr[new Date(startDate).getMonth()] += 1);

    let histogramDataCount = [
      { name: 'J', uv: monthCountArr[0] },
      { name: 'F', uv: monthCountArr[1] },
      { name: 'M', uv: monthCountArr[2] },
      { name: 'A', uv: monthCountArr[3] },
      { name: 'M', uv: monthCountArr[4] },
      { name: 'J', uv: monthCountArr[5] },
      { name: 'J', uv: monthCountArr[6] },
      { name: 'A', uv: monthCountArr[7] },
      { name: 'S', uv: monthCountArr[8] },
      { name: 'O', uv: monthCountArr[9] },
      { name: 'N', uv: monthCountArr[10] },
      { name: 'D', uv: monthCountArr[11] },
    ];

    // set the higest value month to have a white column in the chart
    let max = 0;
    let maxIndex = 0;
    let peakMonth = null;
    for (let i = 0; i < histogramDataCount.length; i++) {
      if (histogramDataCount[i].uv > max) {
        max = histogramDataCount[i].uv;
        maxIndex = i;
      }
    }
    if (maxIndex === 0) { peakMonth = "January" }
    else if (maxIndex === 1) { peakMonth = "February" }
    else if (maxIndex === 2) { peakMonth = "March" }
    else if (maxIndex === 3) { peakMonth = "April" }
    else if (maxIndex === 4) { peakMonth = "May" }
    else if (maxIndex === 5) { peakMonth = "June" }
    else if (maxIndex === 6) { peakMonth = "July" }
    else if (maxIndex === 7) { peakMonth = "August" }
    else if (maxIndex === 8) { peakMonth = "September" }
    else if (maxIndex === 9) { peakMonth = "October" }
    else if (maxIndex === 10) { peakMonth = "November" }
    else if (maxIndex === 11) { peakMonth = "December" }


    histogramDataCount[maxIndex]['fill'] = "#ffffff";

    // console.log("histogramDataCount: ", histogramDataCount);
    return { histogramDataCount, peakMonth };
  }

  //
  const countUniqueStartDates = (arr) => {
    const unique = [];
    arr.forEach(val => {
      if (!unique.includes(val.startDate)) {
        unique.push(val.startDate);
      }
    });
    return unique.length;
  }

  const getHeroValueWidth = () => {

    //  ? 255 : 385

    // Small Screen - 3 digit
    if (yearResults?.rideCount?.toString().length > 2 && width <= 768) {
      return "200px";
    }

    // Small Screen - 1-2 digit
    if (yearResults?.rideCount?.toString().length <= 2 && width <= 768) {
      return "305px";
    }

    // Large Screen - 3 digit
    if (yearResults?.rideCount?.toString().length > 2 && width > 768) {
      return "255px";
    }

    // Large Screen - 1-2 digit
    if (yearResults?.rideCount?.toString().length <= 2 && width > 768) {
      return "385px";
    }

    return "255px";
  }


  const styles = {
    bgContainer: {
      width: "100%",
      background: "linear-gradient(#3f3f3f, #292929)",
    },
    container: {
      display: "block",
      width: "100%",
      height: "svh",
      maxWidth: "500px",
      margin: "0 auto",
      // padding: '15px',
      background: "linear-gradient(#3f3f3f, #292929)",
      // fontFamily: "trim-semibold",
      // fontWeight: "bold",
      color: "white",
    },

  };

  const CardNoSessions = () => {

    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        background: "#000000",
      },
      header: {
        fontFamily: "trim-regular",
        fontSize: "2em",
        color: "#ffffff",
      },
      body: {
        fontFamily: "trim-regular",
        fontSize: "1.5em",
        color: "#ffffff",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        color: "#B3FF00",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
    }

    return (
      <>
        <div style={styles.container}>

          <img
            src={wattbikeLogo}
            alt="Wattbike Logo"
            style={styles.logo}
          />

          {/* <div style={styles.header}>
            Card <br />
            <span style={styles.bold}>eight,</span>
          </div> */}

          <div style={styles.body}>
            No Hub sessions found for 2024 on this account.<br /><br />
            Keep an eye out for some great content to get you going in January, let's make <span style={styles.bold}>2025 your year to shine </span>on the Wattbike Hub!

          </div>

        </div>
      </>
    )
  }

  const CardScreenshot = () => {

    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "0px",
      },
      logo: {
        width: "30%",
        marginRight: "20px",
        display: "inline-block",
        verticalAlign: "middle",
      },
      headerRow: {
        // display: "flex",
        alignItems: "center", /* aligns the items vertically */
        background: "#EE1D23",
        height: "6svh",
        fontFamily: "trim-semibold",
        fontWeight: "bold",

        color: "#FFFFFF",
        textAlign: "center",
        verticalAlign: "middle",
        paddingTop: "5px",

      },
      headerText: {
        display: "inline-block",
        fontFamily: "trim-semibold",
        fontSize: "4em",
        verticalAlign: "middle",
        paddingTop: "5px",
      },
      row: {
        display: "inline-block",
        fontFamily: "trim-semibold",
        fontSize: "4em",
        verticalAlign: "middle",
        paddingTop: "5px",
      },
      row20: {
        height: "20svh",
        background: "#999999",
      },
      row25: {
        height: "25svh",
        background: "#999999",
      },
      row30: {
        height: "30svh",
        background: "#999999",
      },
      row50: {
        height: "44svh",
        background: "#333333",
      },
      box1: {
        background: "#ffffff",
        fontFamily: "trim-semibold",
        fontSize: "2.4em",
        lineHeight: "1.8em",
      },
      box2: {
        background: "#231f20",
        fontFamily: "trim-semibold",
        fontSize: "3em",
        color: "#ffffff",
      },
      box3: {
        background: "#b2ff01",
        height: "100%",
        paddingTop: "40px",
      },
      box3Hero: {
        fontFamily: "trim-semibold",
        fontSize: "23em",
        lineHeight: ".8em",
      },
      box3Subtitle: {
        fontFamily: "trim-semibold",
        fontSize: "5.5em",
        lineHeight: "1em",
      },
      box4: {
        background: "#494949",
      },
      box5: {
        background: "#000000",
        color: "#ffffff",
        padding: "25px"
      },
      box5Value: {
        fontFamily: "trim-semibold",
        // fontSize: "8em",
        lineHeight: ".8em",
      },
      box5Label: {
        fontFamily: "trim-semibold",
        fontSize: "2.5em",
        lineHeight: "1em",
        marginBottom: "40px",
      },
      box6: {
        background: "#ed1b24",
      },
      box6Title: {
        fontFamily: "trim-regular",
        fontSize: "4em",
        color: "#ffffff",
        padding: "15px",
        lineHeight: "1.2em",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontSize: "1.2em",
      },

    }

    return (
      <>
        <div id='screenshot' style={styles.container}>

          <div style={styles.headerRow}>
            {/* <div style={styles.headerContent}> */}
            <img
              src={wattbikeLogo}
              alt="Wattbike Logo"
              style={styles.logo}
            />
            <span style={styles.headerText}> 2024 WRAPPED</span>
            {/* </div> */}
          </div>

          <Container fluid={true} >

            <Row noGutters style={styles.row30}>
              {/* Ride Count */}
              <Col style={styles.box3} sm={8}>
                <span style={styles.box3Hero} >{yearResults.rideCount}</span><br />
                <span style={styles.box3Subtitle} >rides</span>
              </Col>
              {/* Workouts, climb, test... */}
              <Col style={styles.box4} sm={4}>
                <ul style={{ padding: 0, textAlign: "center" }}>
                  <li>
                    <img
                      src={iconWorkout}
                      alt="Icon"
                      style={{ width: "120px" }}
                    /><br />
                    {/* <div style={styles.numbersRow}><span>{processedResults?.quickRideCount}</span> QUICK RIDE</div> */}
                    <span style={{ color: "white", fontSize: "2.5em", lineHeight: "1em" }}>{yearResults.rideCount - processedResults?.quickRideCount} WORKOUTS</span>
                    <br /><br />
                  </li>
                  {/* <li>
                    <img
                      src={iconClimb}
                      alt="Icon"
                      style={{ width: "120px" }}
                    /><br />
                    <div><span style={{ color: "white", fontSize: "2em", lineHeight: "1em" }}>{processedResults?.climbCount} CLIMBS</span>  </div>
                  </li> */}
                  <li>
                    <img
                      src={iconRace}
                      alt="Icon"
                      style={{ width: "120px" }}
                    /><br />
                    <span style={{ color: "white", fontSize: "2.5em", lineHeight: "1em" }}>{processedResults?.challengesCount} CHALLENGES</span>
                    <br /><br />
                  </li>
                  <li>
                    <img
                      src={iconTest}
                      alt="Icon"
                      style={{ width: "120px" }}
                    /><br />
                    <span style={{ color: "white", fontSize: "2.5em", lineHeight: "1em" }}>{processedResults?.testsCount} TESTS</span>
                    <br /><br />
                  </li>
                </ul>
              </Col>
            </Row>

            <Row noGutters style={styles.row50}>
              {/* stats */}
              <Col style={styles.box5} sm={3}>
                <div style={{ fontFamily: "trim-semibold", fontSize: "9em", lineHeight: "1em" }}>{processedResults.activeDays}</div>
                <div style={styles.box5Label}>ACTIVE DAYS</div>
                <div style={{ fontFamily: "trim-semibold", fontSize: "8em", lineHeight: "1em", color: "#b2ff01" }}>{yearResults.totalTimeHours.toFixed(0)}</div>
                <div style={styles.box5Label}>HRS </div>
                <div style={{ fontFamily: "trim-semibold", fontSize: "5em", lineHeight: "1em" }}>{yearResults.totalDistanceKm.toFixed(0)}</div>
                <div style={styles.box5Label}>KM </div>
                <div style={{ fontFamily: "trim-semibold", fontSize: "4em", lineHeight: "1em", color: "#b2ff01" }}>{yearResults.revolutionsCount.toFixed(0)}</div>
                <div style={styles.box5Label}>REVS</div>
              </Col>
              {/* Histogram */}
              <Col style={styles.box6} sm={9}>
                <h2 style={styles.box6Title}>Your <br /><span style={styles.bold}>busiest</span> month</h2>
                <ResponsiveContainer width={"100%"} height={610}>
                  <BarChart style={{ padding: 0, margin: 0, width: "100%", height: "100%" }} data={processedResults.histogramDataCount}>
                    <Bar isAnimationActive={false} dataKey="uv" fill="#000000" barSize={38} radius={[10, 10, 10, 10]} />
                    <XAxis dataKey="name" dy={0} axisLine={false} tickLine={false} style={{ fontSize: "40px", fill: '#000000' }} />
                  </BarChart>
                </ResponsiveContainer>
              </Col>
            </Row>
            <Row style={styles.row20}>
              {/* Workouts */}
              <Col style={styles.box1} sm={6}>
                {processedResults.wordCloudWorkoutsSorted[0]?.count && <>{processedResults.wordCloudWorkoutsSorted[0]?.count} x {processedResults.wordCloudWorkoutsSorted[0]?.title.substring(0, 14) + ".."}<br /></>}
                {processedResults.wordCloudWorkoutsSorted[1]?.count && <>{processedResults.wordCloudWorkoutsSorted[1]?.count} x {processedResults.wordCloudWorkoutsSorted[1]?.title.substring(0, 14) + ".."}<br /></>}
                {processedResults.wordCloudWorkoutsSorted[2]?.count && <>{processedResults.wordCloudWorkoutsSorted[2]?.count} x {processedResults.wordCloudWorkoutsSorted[2]?.title.substring(0, 14) + ".."}<br /></>}
                {processedResults.wordCloudWorkoutsSorted[3]?.count && <>{processedResults.wordCloudWorkoutsSorted[3]?.count} x {processedResults.wordCloudWorkoutsSorted[3]?.title.substring(0, 14) + ".."}<br /></>}
                {processedResults.wordCloudWorkoutsSorted[4]?.count && <>{processedResults.wordCloudWorkoutsSorted[4]?.count} x {processedResults.wordCloudWorkoutsSorted[4]?.title.substring(0, 14) + ".."}<br /></>}
              </Col>
              {/* Categories */}
              <Col style={styles.box2} sm={6}>
                {processedResults.wordCloudCategoriesSorted[0]?.count && <>{processedResults.wordCloudCategoriesSorted[0]?.count} x {processedResults.wordCloudCategoriesSorted[0]?.title}<br /></>}
                {processedResults.wordCloudCategoriesSorted[1]?.count && <>{processedResults.wordCloudCategoriesSorted[1]?.count} x {processedResults.wordCloudCategoriesSorted[1]?.title}<br /></>}
                {processedResults.wordCloudCategoriesSorted[2]?.count && <>{processedResults.wordCloudCategoriesSorted[2]?.count} x {processedResults.wordCloudCategoriesSorted[2]?.title}<br /></>}
                {processedResults.wordCloudCategoriesSorted[3]?.count && <>{processedResults.wordCloudCategoriesSorted[3]?.count} x {processedResults.wordCloudCategoriesSorted[3]?.title}<br /></>}
                {processedResults.wordCloudCategoriesSorted[4]?.count && <>{processedResults.wordCloudCategoriesSorted[4]?.count} x {processedResults.wordCloudCategoriesSorted[4]?.title}<br /></>}
              </Col>
            </Row>
          </Container>
        </div>
      </>
    )
  }

  const CardOne = () => {

    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        // fontFamily: "trim-semibold",
        // fontFamily: " worksans-light",
        // background: "linear-gradient(#3f3f3f, #292929)",
        background: "#EE1D23",
      },
      header: {
        // fontFamily: " worksans-medium",
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "3em",
      },
      body: {
        // fontFamily: " worksans-light",
        fontFamily: "trim-regular",
        // fontWeight: "bold",
        fontSize: "3.5em",
      },
      logo: {
        width: "200px",
        marginBottom: "20px",
        display: "block",
      },

    }

    return (
      <>
        <div style={styles.container}>
          <img
            src={wattbikeLogo}
            alt="Wattbike Logo"
            style={styles.logo}
          />

          <span style={styles.header}>
            you put in,
          </span><br />
          <span style={styles.body}>
            THE WORK<br />
            THE WATTS<br />
            THE ENERGY<br />
            THE SWEAT<br />
          </span>
        </div>
      </>
    )
  }

  const CardTwo = () => {

    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        background: "#EE1D23",
      },
      chart: {
        position: "absolute",
        bottom: "40px",
        width: "95%"
      },
      header: {
        fontFamily: "trim-regular",
        fontSize: "2.2em",
      },
      body: {
        fontFamily: "trim-regular",

        fontSize: "3em",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "1.3em",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
    }

    return (
      <>
        <div style={styles.container}>

          <img
            src={wattbikeLogo}
            alt="Wattbike Logo"
            style={styles.logo}
          />

          <div style={styles.header}>
            you <span style={styles.bold}>smashed </span> <br />it in <span style={styles.bold}>{processedResults.peakMonth}</span>...
          </div>

          <div style={styles.body}>
            {
              width > 768 ?
                <div style={styles.chart}>
                  <ResponsiveContainer width={"100%"} height={450}>
                    <BarChart style={{ padding: "0px", marginBottom: "-8em", width: "100%", height: "100%" }} width={150} height={150} padding={0} margin={0} data={processedResults.histogramDataCount}>
                      <Bar animationDuration={3000} dataKey="uv" fill="#000000" barSize={16} radius={[5, 5, 5, 5]} />
                      <XAxis dataKey="name" dy={-5} axisLine={false} tickLine={false} style={{ fontSize: "18px", fill: '#000000' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div> :
                <div style={styles.chart}>
                  <ResponsiveContainer width={"100%"} height={450}>
                    <BarChart style={{ padding: 0, margin: 0, width: "100%", height: "100%" }} width={150} height={210} data={processedResults.histogramDataCount}>
                      <Bar animationDuration={3000} dataKey="uv" fill="#000000" barSize={16} radius={[6, 6, 6, 6]} />
                      <XAxis dataKey="name" dy={5} axisLine={false} tickLine={false} style={{ fontSize: "20px", fill: '#000000' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
            }
          </div>
        </div>
      </>
    )
  }

  const CardThree = () => {
    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        background: "#B3FF00",
      },
      header: {
        fontFamily: "trim-regular",
        fontSize: "2em",
        color: "#000000",
        marginBottom: "40px"
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "1.3em",
      },
      body: {
        fontFamily: "trim-regular",
        fontSize: "2.5em",
        color: "#000000",
        lineHeight: "1em"
      },
      heroValue: {
        display: "inline-block",
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "1.6em",
        lineHeight: "1em"
        // color: "#EE1D23",
      },
      heroLablel: {
        display: "inline-block",
        fontFamily: "trim-regular",
        fontWeight: "bold",
        fontSize: ".8em",
        marginTop: "-60px",
        marginBottom: "30px",
        // color: "#EE1D23",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
    }

    return (
      <>
        <div style={styles.container}>

          <img
            src={wattbikeLogoBlack}
            alt="Wattbike Logo"
            style={styles.logo}
          />

          <div style={styles.header}>
            and <span style={styles.bold}>loved </span> <br />
            <span style={styles.bold}>the variety...</span>
          </div>

          <div style={styles.body}>
            {processedResults.wordCloudCategoriesSorted[0]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudCategoriesSorted[0]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudCategoriesSorted[0]?.title}</span><br /></>}
            {processedResults.wordCloudCategoriesSorted[1]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudCategoriesSorted[1]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudCategoriesSorted[1]?.title}</span><br /></>}
            {processedResults.wordCloudCategoriesSorted[2]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudCategoriesSorted[2]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudCategoriesSorted[2]?.title}</span><br /></>}
            {processedResults.wordCloudCategoriesSorted[3]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudCategoriesSorted[3]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudCategoriesSorted[3]?.title}</span><br /></>}
            {processedResults.wordCloudCategoriesSorted[4]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudCategoriesSorted[4]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudCategoriesSorted[4]?.title}</span><br /></>}
          </div >

        </div >
      </>
    )
  }

  const CardNine = () => {
    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        background: "#B3FF00",
      },
      header: {
        fontFamily: "trim-regular",
        fontSize: "2em",
        color: "#000000",
        marginBottom: "40px"
      },
      body: {
        fontFamily: "trim-regular",
        fontSize: "2.5em",
        color: "#000000",
        lineHeight: "1em"
      },
      heroValue: {
        display: "inline-block",
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "1.6em",
        lineHeight: "1em"
        // color: "#EE1D23",
      },
      heroLablel: {
        display: "inline-block",
        fontFamily: "trim-regular",
        fontWeight: "bold",
        fontSize: ".5em",
        marginTop: "-60px",
        marginBottom: "30px",
        // color: "#EE1D23",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "1.2em",
      },
    }

    return (
      <>
        <div style={styles.container}>

          <img
            src={wattbikeLogoBlack}
            alt="Wattbike Logo"
            style={styles.logo}
          />

          <div style={styles.header}>
            With your <span style={styles.bold}>favourite <br />
              workouts...</span>
          </div>

          <div style={styles.body}>
            {processedResults.wordCloudWorkoutsSorted[0]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudWorkoutsSorted[0]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudWorkoutsSorted[0]?.title.substring(0, 18) + "..."}</span><br /></>}
            {processedResults.wordCloudWorkoutsSorted[1]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudWorkoutsSorted[1]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudWorkoutsSorted[1]?.title.substring(0, 18) + "..."}</span><br /></>}
            {processedResults.wordCloudWorkoutsSorted[2]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudWorkoutsSorted[2]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudWorkoutsSorted[2]?.title.substring(0, 18) + "..."}</span><br /></>}
            {processedResults.wordCloudWorkoutsSorted[3]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudWorkoutsSorted[3]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudWorkoutsSorted[3]?.title.substring(0, 18) + "..."}</span><br /></>}
            {processedResults.wordCloudWorkoutsSorted[4]?.count && <><span style={styles.heroValue}>{processedResults.wordCloudWorkoutsSorted[4]?.count}</span><span style={styles.heroLablel}> x {processedResults.wordCloudWorkoutsSorted[4]?.title.substring(0, 18) + "..."}</span><br /></>}
          </div >

        </div >
      </>
    )
  }

  const CardFour = () => {
    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        background: "#FFFFFF",
      },
      topContainer: {
        height: "50vh",
        width: "100%",
        padding: "10px",
        background: "#FFFFFF",
      },
      bottomContainer: {
        height: "50vh",
        width: "100%",
        padding: "0px",
        background: "#FFFFFF",
      },
      header: {
        fontFamily: "trim-semibold",
        fontSize: "2.5em",
        color: "#000000",
      },
      body: {
        fontFamily: "trim-regular",
        fontSize: "4.5em",
        color: "#000000",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "3em",
        color: "#EE1D23",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
      chart: {
        width: "101%",
        position: "absolute",
        bottom: "0px",
        marginBottom: "-1px",
      },
    }

    return (
      <>
        <div style={styles.container}>
          <div style={styles.topContainer}>

            <div style={styles.header}>
              hitting highs of <br />
              <span style={styles.bold}> {processedResults.maxFtp}</span> FTP
            </div>

          </div>
          <div style={styles.bottomContainer}>

            <div style={styles.chart}>
              {/* <AreaChart width={400} height={450} data={processedResults.chartDataPowerKg}>
              <Area type="monotone" dataKey="uv" fillOpacity={1} fill="#000000" stroke={false} />
            </AreaChart> */}

              <ResponsiveContainer width={"100%"} height={450}>
                <AreaChart width={400} height={250} margin={{ top: 0, left: 0, right: 0, bottom: 0 }} data={processedResults.chartDataFtp}>
                  <Area type="monotone" dataKey="uv" fillOpacity={1} fill="#000000" stroke={false} />
                </AreaChart>
              </ResponsiveContainer>

            </div>

          </div>
        </div>
      </>
    )
  }

  const CardFive = () => {
    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        background: "#FFFFFF",
      },
      topContainer: {
        height: "50vh",
        width: "100%",
        padding: "10px",
        background: "#FFFFFF",
      },
      bottomContainer: {
        height: "50vh",
        width: "100%",
        padding: "0px",
        background: "#FFFFFF",
      },
      header: {
        fontFamily: "trim-semibold",
        fontSize: "2.5em",
        color: "#000000",
        lineHeight: "1.6em",
      },
      body: {
        fontFamily: "trim-regular",
        fontSize: "4.5em",
        color: "#000000",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "3em",
        color: "#EE1D23",

      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
      chart: {
        width: "101%",
        position: "absolute",
        bottom: "0px",
        marginBottom: "-1px",
      },
    }

    return (
      <>
        <div style={styles.container}>
          <div style={styles.topContainer}>

            <div style={styles.header}>
              and <br />
              <span style={styles.bold}> {processedResults.maxPower}</span> peak Watts
            </div>

          </div>
          <div style={styles.bottomContainer}>

            <div style={styles.chart}>

              <ResponsiveContainer width={"100%"} height={450}>
                <AreaChart width={400} height={450} margin={{ top: 0, left: 0, right: 0, bottom: 0 }} data={processedResults.chartDataPowerMax}>
                  <Area type="monotone" dataKey="uv" fillOpacity={1} fill="#000000" stroke={false} />
                </AreaChart>
              </ResponsiveContainer>

            </div>

          </div>
        </div>
      </>
    )
  }

  const CardSix = () => {
    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        background: "#000000",
      },
      header: {
        fontFamily: "trim-regular",
        fontSize: "2em",
        color: "#ffffff",
        marginBottom: "40px"
      },
      body: {
        fontFamily: "trim-semibold",
        fontSize: "2.5em",
        color: "#ffffff",
        lineHeight: "1.5em",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        color: "#EE1D23",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
      heroNumber: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: "1.6em",
        color: "#ffffff",
        lineHeight: "0.5em",
      },
      heroLabel: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        fontSize: ".5em",
        color: "#EE1D23",
        paddingBottom: "15px",
      },
    }

    return (
      <>
        <div style={styles.container}>

          <div style={styles.header}>
            you really pumped up those numbers...<br />
          </div>

          <div style={styles.body}>
            <div style={styles.heroNumber}>{processedResults.activeDays}</div>
            <div style={styles.heroLabel}>ACTIVE DAYS</div>
            <div style={styles.heroNumber}>{yearResults.totalTimeHours.toFixed(1)}</div>
            <div style={styles.heroLabel}>HRS </div>
            <div style={styles.heroNumber}>{yearResults.totalDistanceKm.toFixed(0)}</div>
            <div style={styles.heroLabel}>KM </div>
            <div style={styles.heroNumber}>{yearResults.revolutionsCount.toFixed(0)}</div>
            <div style={styles.heroLabel}>REVS</div>
          </div>

        </div>
      </>
    )
  }

  const CardSeven = () => {
    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        background: "#FFFFFF",
        backgroundImage: `url(${confetti})`,
        backgroundSize: "1600px 800px",
      },
      heroNumber: {
        fontFamily: "trim-semibold",
        fontSize: "10em",
        lineHeight: "1em",
        color: "#000000",
        textShadow: "5px 5px 10px #ffffff",
      },
      header: {
        fontFamily: "trim-semibold",
        fontSize: "4em",
        lineHeight: "1em",
        color: "#000000",
        textShadow: "5px 5px 10px #ffffff",
      },
      subheader: {
        fontFamily: "trim-regular",
        fontSize: "4em",
        color: "#EE1D23",
        textShadow: "5px 5px 10px #ffffff",
      },
      body: {
        fontFamily: "trim-regular",
        fontSize: "4.5em",
        color: "#000000",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        color: "#EE1D23",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
    }

    return (
      <>
        <div style={styles.container}>

          <div style={styles.header}>
            and over <br />

          </div>

          <div style={styles.heroNumber}>
            {yearResults.rideCount}
          </div>
          <div style={styles.subheader}>
            rides...
          </div>

        </div>
      </>
    )
  }

  const CardEight = () => {
    const styles = {
      container: {
        height: "100svh",
        width: "100%",
        padding: "10px",
        background: "#000000",
      },
      header: {
        fontFamily: "trim-regular",
        fontSize: "2em",
        color: "#ffffff",
      },
      body: {
        fontFamily: "trim-regular",
        fontSize: "1.5em",
        color: "#ffffff",
      },
      bold: {
        fontFamily: "trim-semibold",
        fontWeight: "bold",
        color: "#B3FF00",
      },
      logo: {
        width: "100px",
        marginBottom: "20px"
      },
    }

    return (
      <>
        <div style={styles.container}>

          <img
            src={wattbikeLogo}
            alt="Wattbike Logo"
            style={styles.logo}
          />

          {/* <div style={styles.header}>
            Card <br />
            <span style={styles.bold}>eight,</span>
          </div> */}

          <div style={styles.body}>
            Well done on your 2024, now let's make <span style={styles.bold}>2025 even better!</span><br /><br />
            {downloadLink && <>
              <a href={downloadLink} target="_blank" rel="noreferrer" >Download for Instagram Story</a><br /><br />
              <a href={downloadLink} target="_blank" rel="noreferrer" >
                <img
                  src={downloadLink}
                  alt="Icon"
                  style={{ width: "160px" }}
                />
              </a>
            </>}

          </div>

        </div>
      </>
    )
  }

  if (loading) {
    return (
      <Loading label={"general-loading"} />
    )
  } else if (noSessions) {
    return (
      <CardNoSessions />
    )
  } else if (isScreenshot) {
    return (
      <CardScreenshot />
    )
  } else {
    return (
      <>
        <div style={styles.bgContainer}>
          <div style={styles.container}>
            <Carousel>
              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardOne />
              </Carousel.Item>
              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardTwo />
              </Carousel.Item>
              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardThree />
              </Carousel.Item>
              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardNine />
              </Carousel.Item>
              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardSeven />
              </Carousel.Item>
              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardSix />
              </Carousel.Item>


              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardFour />
              </Carousel.Item>
              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardFive />
              </Carousel.Item>


              <Carousel.Item style={{ height: "100svh" }} interval={500000} >
                <CardEight />
              </Carousel.Item>
            </Carousel>
          </div>
        </div>
      </>
      // <>

      //   <Container>
      //     {/* Logo */}
      //     <div style={styles.header}>
      //       <img
      //         src={wattbikeLogo}
      //         alt="Wattbike Logo"
      //         style={styles.logoHeader}
      //       />
      //       Your 2024 on Wattbike
      //     </div>
      //     <Row>
      //       {/* Column 1*/}
      //       <Col md={6}>
      //         {/* Top row */}
      //         <div style={{ display: "block", width: "100%" }}>
      //           <div style={styles.heroValue} >{yearResults.rideCount}</div>
      //           <div style={styles.heroUnit}>rides</div>
      //         </div>

      //         <div style={styles.rowSpacer}></div>

      //         {/* Numbers row */}
      //         <div style={styles.fullRow}>
      //           <div style={styles.numbersRow}><span style={styles.numbersRowBold}>{processedResults.activeDays}</span> ACTIVE DAYS</div>
      //           <div style={styles.numbersRow}><span style={styles.numbersRowBold}>{yearResults.totalTimeHours.toFixed(1)}</span> HOURS RIDDEN</div>
      //           <div style={styles.numbersRow}><span style={styles.numbersRowBold}>{yearResults.totalDistanceKm.toFixed(0)}</span> KM COVERED</div>
      //           <div style={styles.numbersRow}><span style={styles.numbersRowBold}>{yearResults.revolutionsCount.toFixed(0)}</span> REVS</div>
      //         </div>

      //         <div style={styles.rowSpacer}></div>

      //         {/* Icon grid */}
      //         <div style={styles.fullRow}>
      //           <ul style={styles.grid}>
      //             <li style={styles.gridLi}>
      //               <img
      //                 src={iconWorkout}
      //                 alt="Icon"
      //                 style={styles.gridImg}
      //               /><br />
      //               {/* <div style={styles.numbersRow}><span>{processedResults?.quickRideCount}</span> QUICK RIDE</div> */}
      //               <div><span style={{ color: "white", fontSize: "3em", lineHeight: "1em" }}>{yearResults.rideCount - processedResults?.quickRideCount}</span><br /> <span style={{ fontSize: "1em", color: "#bdbdbd" }}>WORKOUTS</span></div>
      //             </li>
      //             <li style={styles.gridLi}>
      //               <img
      //                 src={iconClimb}
      //                 alt="Icon"
      //                 style={styles.gridImg}
      //               /><br />
      //               <div><span style={{ color: "white", fontSize: "3em", lineHeight: "1em" }}>{processedResults?.climbCount}</span><br /><span style={{ fontSize: "1em", color: "#bdbdbd" }}>CLIMBS</span>  </div>
      //             </li>
      //             <li style={styles.gridLi}>
      //               <img
      //                 src={iconRace}
      //                 alt="Icon"
      //                 style={styles.gridImg}
      //               /><br />
      //               <div><span style={{ color: "white", fontSize: "3em", lineHeight: "1em" }}>{processedResults?.challengesCount}</span><br /> <span style={{ fontSize: "1em", color: "#bdbdbd" }}>CHALLENGES</span> </div>
      //             </li>
      //             <li style={styles.gridLi}>
      //               <img
      //                 src={iconTest}
      //                 alt="Icon"
      //                 style={styles.gridImg}
      //               /><br />
      //               <div><span style={{ color: "white", fontSize: "3em", lineHeight: "1em" }}>{processedResults?.testsCount}</span><br /> <span style={{ fontSize: "1em", color: "#bdbdbd" }}>TESTS</span>  </div>
      //             </li>
      //           </ul>
      //         </div>

      //       </Col>
      //       <Col md={6}>
      //         {/* Column 2 */}

      //         {width < 768 ? <div style={styles.rowSpacer}></div> : <> </>}

      //         {/* Workouts List Row */}
      //         <div style={styles.fullRow}>
      //           <h2>Categories</h2>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudCategoriesSorted[0]?.count}</span>x {processedResults.wordCloudCategoriesSorted[0]?.title}</div>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudCategoriesSorted[1]?.count}</span>x {processedResults.wordCloudCategoriesSorted[1]?.title}</div>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudCategoriesSorted[2]?.count}</span>x {processedResults.wordCloudCategoriesSorted[2]?.title}</div>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudCategoriesSorted[3]?.count}</span>x {processedResults.wordCloudCategoriesSorted[3]?.title}</div>
      //         </div>

      //         <div style={styles.rowSpacer}></div>

      //         {/* Categories List */}
      //         <div style={styles.fullRow}>
      //           <h2>Workouts</h2>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudWorkoutsSorted[0]?.count}</span>x {processedResults.wordCloudWorkoutsSorted[0]?.title}</div>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudWorkoutsSorted[1]?.count}</span>x {processedResults.wordCloudWorkoutsSorted[1]?.title}</div>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudWorkoutsSorted[2]?.count}</span>x {processedResults.wordCloudWorkoutsSorted[2]?.title}</div>
      //           <div style={styles.numbersList}><span style={styles.numbersListBold}>{processedResults.wordCloudWorkoutsSorted[3]?.count}</span>x {processedResults.wordCloudWorkoutsSorted[3]?.title}</div>
      //         </div>


      //         <div style={styles.rowSpacer}></div>

      //         {/* Charts rows */}

      //         {/* <div style={styles.fullRow}>
      //           <h2 style={styles.chartTitle}>W/kg</h2>
      //           <LineChart width={400} height={100} data={processedResults.chartDataPowerKg}>
      //             <Line type="monotone" dataKey="uv" stroke="#fff" dot={false} />
      //           </LineChart>
      //         </div>

      //         <div style={styles.fullRow}>
      //           <h2 style={styles.chartTitle}>Weight</h2>
      //           <LineChart width={400} height={100} data={processedResults.chartDataWeight}>
      //             <Line type="monotone" dataKey="uv" stroke="#fff" dot={false} />
      //           </LineChart>
      //         </div> */}

      //         <div style={styles.chartRow}>
      //           <h2 style={styles.chartTitle}>Avg Power</h2>
      //           <ResponsiveContainer width="100%" height={110}>
      //             <LineChart width={380} height={100} data={processedResults.chartDataPowerAvg}>
      //               <Line type="monotone" dataKey="uv" stroke="#fff" dot={false} />
      //             </LineChart>
      //           </ResponsiveContainer>
      //         </div>

      //         <div style={styles.chartRow}>
      //           <h2 style={styles.chartTitle}>Peak Power</h2>
      //           <ResponsiveContainer width="100%" height={110}>
      //             <LineChart width={380} height={100} data={processedResults.chartDataPowerMax}>
      //               <Line type="monotone" dataKey="uv" stroke="#fff" dot={false} />
      //             </LineChart>
      //           </ResponsiveContainer>
      //         </div>

      //         <div style={styles.chartRow}>
      //           <h2 style={styles.chartTitle}>FTP</h2>
      //           <ResponsiveContainer width="100%" height={110}>
      //             <LineChart width={400} height={100} data={processedResults.chartDataFtp}>
      //               <Line type="monotone" dataKey="uv" stroke="#fff" dot={false} />
      //             </LineChart>
      //           </ResponsiveContainer>
      //         </div>

      //       </Col>
      //     </Row>
      //   </Container>

      //   <div style={styles.rowSpacer}></div>

      //   {/* Histogram */}
      //   {
      //     width > 768 ?
      //       <div style={styles.fullRow}>
      //         <BarChart style={{ padding: "0px", marginBottom: "-8em", width: "100%", height: "100%" }} width={150} height={60} padding={0} margin={0} data={processedResults.histogramDataCount}>
      //           <Bar dataKey="uv" fill="#ee1c26" background={{ fill: '#eee', opacity: .05, radius: 1 }} barSize={3} radius={[.5, .5, .5, .5]} />
      //           <XAxis dataKey="name" dy={-5} axisLine={false} tickLine={false} style={{ fontSize: "3px" }} />
      //         </BarChart>
      //       </div> :
      //       <div style={styles.fullRow}>
      //         <BarChart style={{ padding: 0, margin: 0, width: "100%", height: "100%" }} width={150} height={110} data={processedResults.histogramDataCount}>
      //           <Bar dataKey="uv" fill="#ee1c26" background={{ fill: '#eee', opacity: .05, radius: 1 }} barSize={6} radius={[2, 2, 2, 2]} />
      //           <XAxis dataKey="name" axisLine={false} tickLine={false} style={{ fontSize: "8px" }} />
      //         </BarChart>
      //       </div>
      //   }

      //   {/* Logo */}
      //   <img
      //     src={wattbikeLogo}
      //     alt="Wattbike Logo"
      //     style={styles.logo}
      //   />
      // </>
    );
  }

}

//////////////////////////////////////////////////////////////
// Get user sessions report for this year
//////////////////////////////////////////////////////////////
const getUserSessions = async (params = {}) => {
  // compile all options
  let requestOptions = {
    resource: "sessions",
    params: params,
    cache: false,
  };

  // run the query
  let response = await hubLabsGet(requestOptions);
  return response;
};

export default ThisYearSummary;
